import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { api } from '../services/api';
import toast from 'react-hot-toast';

interface FreeTrialFormData {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  company?: string;
  intendedUse: 'business' | 'research' | 'personal';
}

const UpgradePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'premium'>('basic');
  const [formData, setFormData] = useState<FreeTrialFormData>({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: '',
    company: '',
    intendedUse: 'business'
  });

  // Get referral context from URL params
  const fromProduct = searchParams.get('product');
  const fromFeature = searchParams.get('feature');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // For now, just start a free trial without payment
      // In the future, this would integrate with Stripe
      const trialData = {
        ...formData,
        requested_tier: selectedPlan,
        trial_duration_days: 14,
        source: 'upgrade_page',
        referral_context: {
          product: fromProduct,
          feature: fromFeature
        }
      };

      // Call API to start free trial (this endpoint needs to be created)
      await api.startFreeTrial(trialData);

      toast.success(`🎉 Free trial started! You now have access to ${selectedPlan} features for 14 days.`);
      
      // Redirect back to the product they came from or to dashboard
      if (fromProduct) {
        navigate(`/products/${fromProduct}`);
      } else {
        navigate('/');
      }

    } catch (error) {
      console.error('Failed to start trial:', error);
      toast.error('Failed to start trial. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If user is admin, redirect them back with a message
  if (user?.role === 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="text-green-500 text-8xl mb-8">👑</div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Admin Access
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              You already have admin access to all features! No upgrade needed.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Start Your Free Trial
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get 14 days of free access to advanced manufacturing analysis features. 
            No credit card required during trial period.
          </p>
          {fromProduct && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg inline-block">
              <p className="text-sm text-blue-800">
                🔗 Continue analyzing: <span className="font-medium">{fromProduct}</span>
                {fromFeature && ` → ${fromFeature}`}
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Plan Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Your Plan</h2>
              
              <div className="space-y-4">
                <div 
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedPlan === 'basic' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedPlan('basic')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Basic Plan</h3>
                    <span className="text-lg font-bold text-gray-900">$19/mo</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>✓ Tariff impact analysis</li>
                    <li>✓ Component cost breakdown</li>
                    <li>✓ Basic supply chain insights</li>
                    <li>✓ Policy scenario modeling</li>
                  </ul>
                </div>

                <div 
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedPlan === 'premium' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedPlan('premium')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Premium Plan</h3>
                    <span className="text-lg font-bold text-gray-900">$49/mo</span>
                  </div>
                  <div className="mb-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      Most Popular
                    </span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>✓ Everything in Basic</li>
                    <li>✓ Reshoring feasibility analysis</li>
                    <li>✓ Investment ROI calculations</li>
                    <li>✓ Advanced policy modeling</li>
                    <li>✓ Market intelligence reports</li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-medium text-green-900 mb-2">14-Day Free Trial</h4>
                <p className="text-sm text-green-700">
                  Full access to all {selectedPlan} features. Cancel anytime during trial.
                </p>
              </div>
            </div>
          </div>

          {/* Registration Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Trial Registration</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* User Info (if not logged in) */}
                {!isAuthenticated && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <span className="font-medium">Already have an account?</span> 
                      <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="ml-2 text-yellow-900 underline hover:no-underline"
                      >
                        Sign in here
                      </button>
                    </p>
                  </div>
                )}

                {/* Address Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="123 Main St"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="New York"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="NY"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="10001"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Business Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company (Optional)
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Acme Corp"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Intended Use *
                      </label>
                      <select
                        name="intendedUse"
                        value={formData.intendedUse}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="business">Business/Commercial</option>
                        <option value="research">Academic/Research</option>
                        <option value="personal">Personal Interest</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="border-t pt-6">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="terms"
                      required
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600">
                      I agree to the{' '}
                      <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
                    </label>
                  </div>
                </div>

                {/* Submit */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Starting Trial...</span>
                      </div>
                    ) : (
                      `Start 14-Day Free Trial (${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)})`
                    )}
                  </button>
                  
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    No credit card required during trial. You can cancel anytime.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Features Preview */}
        <div className="mt-12 bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            What You'll Get With Your Trial
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="font-medium text-gray-900 mb-2">Real-Time Tariff Analysis</h3>
              <p className="text-sm text-gray-600">
                See exactly how much trade policies add to product costs with live government data
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl mb-4">🏭</div>
              <h3 className="font-medium text-gray-900 mb-2">Reshoring Feasibility</h3>
              <p className="text-sm text-gray-600">
                Analyze the costs and benefits of bringing manufacturing back to the US
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="font-medium text-gray-900 mb-2">Investment Analysis</h3>
              <p className="text-sm text-gray-600">
                Get ROI calculations and financial projections for supply chain decisions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage; 